import { useState } from "react";
import { Loader2, X, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminApi";

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue transition";

export const ChangePasswordModal = ({ onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed successfully.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not change password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <form
        data-testid="change-password-modal"
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-brand-navy" />
            <h2 className="font-heading font-700 text-lg text-brand-navy">Change password</h2>
          </div>
          <button
            type="button"
            data-testid="change-password-close"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Current password</label>
            <input
              data-testid="cp-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputClass}
              placeholder="Current password"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">New password</label>
            <input
              data-testid="cp-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputClass}
              placeholder="At least 8 characters"
              minLength={8}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm new password</label>
            <input
              data-testid="cp-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
              placeholder="Re-enter new password"
              required
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            data-testid="cp-cancel"
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="cp-submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-brand-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-brand-navy-dark text-sm disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Update password
          </button>
        </div>
      </form>
    </div>
  );
};
